import { useCallback, useRef } from "react";

import {
  evaluateInterstitialBreak,
  type InterstitialBreakEvent,
} from "@mobile/audio/interstitialBreakPolicy";

import { showInterstitialAd, type ConsentState } from "./rewardedAds";
import type { WebMonetizationProfile } from "./webMonetization";

function readInterstitialAdUnitPath(): string | null {
  const value = String(import.meta.env.VITE_GOOGLE_AD_MANAGER_INTERSTITIAL_UNIT_PATH || "").trim();
  if (!value || value.includes("ca-app-pub-") || !value.startsWith("/")) return null;
  return value;
}

export function useWebInterstitials(params: {
  consent: ConsentState;
  lastRewardedUnlockAtMs: number | null;
  monetization: WebMonetizationProfile;
}) {
  const lastInterstitialAtMs = useRef<number | null>(null);
  const adUnitPath = readInterstitialAdUnitPath();
  const maybeShow = useCallback(async (event: InterstitialBreakEvent): Promise<boolean> => {
    const nowMs = Date.now();
    const decision = evaluateInterstitialBreak({
      subscriptionTier: params.monetization.subscriptionTier,
      adsEnabled: params.monetization.adsEnabled,
      event,
      nowMs,
      lastInterstitialAtMs: lastInterstitialAtMs.current,
      lastRewardedUnlockAtMs: params.lastRewardedUnlockAtMs,
    });
    if (!decision.shouldShow || !adUnitPath || params.consent === "unknown") {
      return false;
    }
    const shown = await showInterstitialAd({
      adUnitPath,
      consent: params.consent,
      isPremium: params.monetization.subscriptionTier === "premium",
    });
    if (shown) {
      lastInterstitialAtMs.current = nowMs;
    }
    return shown;
  }, [adUnitPath, params.consent, params.lastRewardedUnlockAtMs, params.monetization.adsEnabled, params.monetization.subscriptionTier]);

  return { configured: Boolean(adUnitPath), maybeShow };
}
